import type { Binding } from "@/app/types";
import type { OrgProjectContext } from "./org-project-context/constants";
import { useOrgProjectRouteContext } from "./org-project-context/hooks";

export type OrgProjectScope = OrgProjectContext;
export type PartialOrgProjectScope = Partial<OrgProjectScope>;
export type ResourceScopeParams = {
  scope: PartialOrgProjectScope;
};
export type ResourceKeyParams = ResourceScopeParams & {
  binding?: Binding;
};

/** Primary route-derived scope for request construction and route-coupled UI. */
export function useResourceScope(): OrgProjectScope {
  const { organizationId, projectId } = useOrgProjectRouteContext();

  return { organizationId, projectId };
}

function getBindingKey({ scope, binding = "project" }: ResourceKeyParams) {
  if (binding === "tenant") {
    return { binding } as const;
  }

  return {
    binding,
    projectId: scope.projectId ?? undefined,
  } as const;
}

export function createResourceKeys<TResource extends string>(
  resource: TResource
) {
  const all = ({ scope }: ResourceScopeParams) => {
    const organizationId = scope.organizationId ?? undefined;

    return ["resources", organizationId, resource] as const;
  };

  const lists = (params: ResourceScopeParams) =>
    [...all(params), "list"] as const;

  const list = ({ binding, ...rest }: ResourceKeyParams) =>
    [...lists(rest), getBindingKey({ ...rest, binding })] as const;

  const details = (params: ResourceScopeParams) =>
    [...all(params), "detail"] as const;

  const detail = ({
    id,
    binding,
    ...rest
  }: ResourceKeyParams & { id: string }) =>
    [...details(rest), getBindingKey({ ...rest, binding }), id] as const;

  const singles = (params: ResourceScopeParams) =>
    [...all(params), "single"] as const;

  const single = ({ binding, ...rest }: ResourceKeyParams) =>
    [...singles(rest), getBindingKey({ ...rest, binding })] as const;

  return { all, lists, list, details, detail, singles, single };
}
